import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../components/common/Card/Card';
import Button from '../../components/common/Button/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import CompetitionStatus from '../../components/common/CompetitionStatus/CompetitionStatus';
import { getMyEnrollments } from '../../services/competitionWorkflowService';
import { buildCompetitionCards, formatDeadline, daysChip } from '../../utils/competitionCards';
import styles from './StudentCompetitions.module.css';

/**
 * My Competitions — one card per competition the student is enrolled in.
 *
 * Ordering, the deadline source and "Prioritise next" are in
 * utils/competitionCards.js (shared with the overview page's tab row).
 * "Next step" is the title of the student's nearest-deadline open component
 * (`next_step` from GET /competition-workflow/me/enrollments). The card has no
 * description: the database has no column for one yet.
 */

const StudentCompetitions = () => {
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      setEnrollments(await getMyEnrollments());
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const { cards, priorityId } = useMemo(() => buildCompetitionCards(enrollments), [enrollments]);

  if (loading) {
    return (
      <div className={styles.loading}>
        <LoadingSpinner size="large" message="Loading competitions..." />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1>My Competitions</h1>
          <p className={styles.subtitle}>
            View your enrolled competitions, track upcoming deadlines, and continue
            preparing your applications.
          </p>
        </div>
        {!failed && cards.length > 0 && (
          <span className={styles.count}>
            {cards.length} competition{cards.length === 1 ? '' : 's'} · sorted by nearest deadline
          </span>
        )}
      </div>

      {failed ? (
        <Card>
          <div className={styles.empty}>
            <p>Failed to load your competitions.</p>
            <Button size="small" variant="outline" onClick={load}>Retry</Button>
          </div>
        </Card>
      ) : cards.length === 0 ? (
        <Card>
          <div className={styles.empty}>
            <p>You have not been assigned to any competitions yet.</p>
          </div>
        </Card>
      ) : (
        <div className={styles.grid}>
          {cards.map((c) => {
            const chip = c.days === null ? null : daysChip(c.days);
            // Not started and finished competitions read as "view"; anything in
            // between is work the student is already in the middle of.
            const inProgress = c.status !== 'not_started' && c.status !== 'approved';
            return (
              <Card key={c.id} hover className={styles.card}>
                <div className={styles.cardTop}>
                  <span className={styles.initial} aria-hidden="true">
                    {c.name.trim().charAt(0).toUpperCase()}
                  </span>
                  {c.id === priorityId && <span className={styles.priority}>Prioritise next</span>}
                </div>

                <h3 className={styles.name}>{c.name}</h3>

                <div className={styles.facts}>
                  <div>
                    <span className={styles.eyebrow}>Next step</span>
                    <div className={styles.value}>
                      {c.nextStep || (c.status === 'approved' ? 'All components approved' : '—')}
                    </div>
                  </div>
                  <div>
                    <span className={styles.eyebrow}>Upcoming deadline</span>
                    <div className={styles.value}>
                      {c.date ? formatDeadline(c.date) : 'Deadline not yet added'}
                    </div>
                  </div>
                </div>

                <div className={styles.chips}>
                  {chip ? (
                    <span className={`${styles.chip} ${styles[chip.tone]}`}>{chip.text}</span>
                  ) : (
                    <span className={styles.noDeadline}>Deadline to be announced</span>
                  )}
                  <CompetitionStatus status={c.status} />
                </div>

                <Button
                  fullWidth
                  className={styles.cta}
                  onClick={() => navigate(`/student/competitions/${c.templateId}`)}
                >
                  {inProgress ? 'Continue' : 'View Competition'} →
                </Button>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StudentCompetitions;
